import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { SimpleTooltip } from './design-system';
import { parseFloat } from '../utils/constant';

// Status badge colors and styles - updated to match document rules
const statusStyles = {
    'GOOD': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'AVERAGE': { backgroundColor: '#fef3c7', color: '#d97706' },
    'BELOW AVERAGE': { backgroundColor: '#fecaca', color: '#dc2626' },
    'POOR': { backgroundColor: '#fecaca', color: '#dc2626' },
    'ABOVE AVERAGE': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'EXCELLENT': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'PLAYMAKER': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'LOW ASSIST RATE': { backgroundColor: '#fed7aa', color: '#ea580c' },
    'ELITE BALL SECURITY': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'CARELESS WITH BALL': { backgroundColor: '#fecaca', color: '#dc2626' },
    'DECENT': { backgroundColor: '#fef3c7', color: '#d97706' },
    'DEVELOPMENT AREA': { backgroundColor: '#fecaca', color: '#dc2626' },
    'WEAK': { backgroundColor: '#fed7aa', color: '#ea580c' },
    'HIGH VOLUME SCORER': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'EFFICIENT SCORER': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'VOLUME SHOOTER': { backgroundColor: '#fed7aa', color: '#ea580c' },
    'POSITIVE IMPACT': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'NEUTRAL': { backgroundColor: '#fef3c7', color: '#d97706' },
    'NEEDS WORK': { backgroundColor: '#fecaca', color: '#dc2626' },
    'ELITE TEAM OFFENSE': { backgroundColor: '#bbf7d0', color: '#16a34a' },
    'SOLID': { backgroundColor: '#fef3c7', color: '#d97706' },
    'PRIMARY OPTION': { backgroundColor: '#fed7aa', color: '#ea580c' }
};

// Tooltip definitions
const tooltipDefinitions = {
    'G': "Total number of games played by the player during the season.",
    'MPG': "Average number of minutes played per game by the player.",
    'PPG': "Average number of points scored per game by the player.",
    'Efficiency Rating': "A simplified measure of overall contribution combining various statistical categories.",
    'TOPG': "Average number of turnovers per game by the player.",
    'FPG': "Average number of personal fouls per game by the player.",
    '3-Point Percentage': "A player's predicted three-point shooting percentage against an average opponent, adjusted for usage.",
    '2-Point Percentage': "A player's predicted two-point shooting percentage against an average opponent, adjusted for usage.",
    'Free Throw Percentage': "A player's predicted free throw shooting percentage against an average opponent.",
    'True Shooting %': "A player's predicted true shooting percentage against an average opponent, adjusted for usage.",
    'Effective Field Goal %': "A shooting efficiency metric that adjusts field goal percentage to account for the added value of three-point field goals.",
    'Assist Rate': "A player's predicted assist rate against an average opponent, adjusted for usage. Assist Rate is the percentage of teammate made field goals that the player assisted while on the court.",
    'Turnover Rate': "A player's predicted turnover percentage against an average opponent, adjusted for usage. Turnover percentage is the percentage of offensive possessions that end in the player turning the ball over while on the court.",
    'Playmaking Score': "A player's predicted playmaking score, which combines predicted assist rate and turnover rate to measure a player's ability to create plays for others with the ball in his hands.",
    'Points Per Game': "A player's predicted points per 100 possessions based on predicted shot usage and efficiency.",
    'Field Goal Percentage': "A player's overall field goal shooting percentage combining 2-point and 3-point attempts.",
    'Scoring Efficiency': "A composite metric measuring how efficiently a player scores points.",
    'Offensive BPR': "A player's offensive per-possession value to a team, against an average opponent, as measured by Offensive BPR.",
    "Assist to TO Ratio": "Predicted ratio of assists to turnovers, measuring a player's decision-making and ball security.",
    'Games': "Total number of games played by the player during the season."
};

const OffensiveTab = ({ stats }) => {
    const { theme } = useTheme();
    const isDarkMode = true; // OLED: always dark
    const [season, setSeason] = useState('2023 - 2024 Season');
    const [gameType, setGameType] = useState('All Games');
    const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
    const [gameTypeDropdownOpen, setGameTypeDropdownOpen] = useState(false);
    // Removed custom tooltip states as we're using antd Tooltip

    const seasonOptions = ['2023 - 2024 Season', '2022 - 2023 Season', '2021 - 2022 Season'];
    const gameTypeOptions = ['All Games', 'Regular Season', 'Playoffs', 'Home Games', 'Away Games'];

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setSeasonDropdownOpen(false);
            setGameTypeDropdownOpen(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Removed scroll event listener for custom tooltip

    // Status determination functions for Overall Metrics
    const getMPGStatus = (value) => {
        if (!value) return null;
        if (value < 20) return 'BELOW AVERAGE';
        if (value >= 20 && value < 30) return 'AVERAGE';
        if (value >= 30 && value < 35) return 'GOOD';
        if (value >= 35) return 'ABOVE AVERAGE';
        return null;
    };

    const getPPGStatusOverall = (value) => {
        if (!value) return null;
        if (value < 8) return 'BELOW AVERAGE';
        if (value >= 8 && value < 15) return 'AVERAGE';
        if (value >= 15 && value < 20) return 'GOOD';
        if (value >= 20) return 'ABOVE AVERAGE';
        return null;
    };

    const getEfficiencyRatingStatus = (value) => {
        if (!value) return null;
        if (value < 12) return 'BELOW AVERAGE';
        if (value >= 12 && value < 17) return 'AVERAGE';
        if (value >= 17 && value < 22) return 'GOOD';
        if (value >= 22) return 'ABOVE AVERAGE';
        return null;
    };

    const getTOPGStatus = (value) => {
        if (!value) return null;
        if (value > 4) return 'BELOW AVERAGE';
        if (value >= 2.5 && value <= 4) return 'AVERAGE';
        if (value >= 1.5 && value < 2.5) return 'GOOD';
        if (value < 1.5) return 'ABOVE AVERAGE';
        return null;
    };

    const getFPGStatus = (value) => {
        if (!value) return null;
        if (value > 4) return 'BELOW AVERAGE';
        if (value >= 2.5 && value <= 4) return 'AVERAGE';
        if (value >= 1.5 && value < 2.5) return 'GOOD';
        if (value < 1.5) return 'ABOVE AVERAGE';
        return null;
    };

    // Function to determine 3-point percentage status
    const getThreePointStatus = (value) => {
        if (!value) return null;
        if (value >= 38) return 'GOOD';
        if (value >= 33) return 'AVERAGE';
        if (value >= 30) return 'BELOW AVERAGE';
        return 'POOR';
    };

    // Function to determine 2-point percentage status
    const getTwoPointStatus = (value) => {
        if (!value) return null;
        if (value >= 55) return 'GOOD';
        if (value >= 50) return 'AVERAGE';
        if (value >= 45) return 'BELOW AVERAGE';
        return 'POOR';
    };

    // Function to determine free throw percentage status
    const getFreeThrowStatus = (value) => {
        if (!value) return null;
        if (value >= 80) return 'EXCELLENT';
        if (value >= 70) return 'ABOVE AVERAGE';
        if (value >= 60) return 'AVERAGE';
        return 'POOR';
    };

    // Function to determine true shooting percentage status
    const getTrueShootingStatus = (value) => {
        if (!value) return null;
        if (value >= 58) return 'GOOD';
        if (value >= 53) return 'AVERAGE';
        if (value >= 48) return 'BELOW AVERAGE';
        return 'POOR';
    };

    // Function to determine effective field goal percentage status
    const getEffectiveFieldGoalStatus = (value) => {
        if (!value) return null;
        if (value >= 55) return 'GOOD';
        if (value >= 50) return 'AVERAGE';
        if (value >= 45) return 'BELOW AVERAGE';
        return 'POOR';
    };

    // Function to determine points per game status
    const getPointsPerGameStatus = (ppg, fga) => {
        if (ppg >= 15 && fga >= 12) return 'HIGH VOLUME SCORER';
        return null;
    };

    // Function to determine PPG status for basic metrics
    const getPPGStatus = (ppg, fga) => {
        if (ppg >= 15 && fga >= 12) return 'HIGH VOLUME SCORER';
        return null;
    };

    // Function to determine field goal percentage status
    const getFieldGoalStatus = (fgPct) => {
        if (!fgPct) return null;
        if (fgPct >= 45) return 'EFFICIENT SCORER';
        return null;
    };

    // Function to determine scoring efficiency status
    const getScoringEfficiencyStatus = (fgPct, tsPct, fga) => {
        if (fgPct >= 45 || tsPct >= 55) return 'EFFICIENT SCORER';
        if (fga > 12 && tsPct < 50) return 'VOLUME SHOOTER';
        return null;
    };

    // Function to determine assist rate status
    const getAssistRateStatus = (value) => {
        if (!value) return null;
        if (value >= 20) return 'PLAYMAKER';
        if (value >= 10) return 'AVERAGE';
        return 'LOW ASSIST RATE';
    };

    // Function to determine turnover rate status
    const getTurnoverRateStatus = (value) => {
        if (!value) return null;
        if (value < 5) return 'ELITE BALL SECURITY';
        if (value <= 10) return 'AVERAGE';
        return 'CARELESS WITH BALL';
    };

    // Function to determine playmaking score status
    const getPlaymakingStatus = (value) => {
        if (!value && value !== 0) return null;
        if (value >= 5) return 'EXCELLENT';
        if (value >= 0) return 'DECENT';
        if (value >= -5) return 'DEVELOPMENT AREA';
        return 'WEAK';
    };

    // Function to determine assist to turnover ratio status
    const getAssistToTurnoverStatus = (value) => {
        if (!value) return null;
        if (value >= 2.5) return 'EXCELLENT';
        if (value >= 1.5) return 'GOOD';
        return 'WEAK';
    };

    // Function to determine offensive BPR status
    const getOffensiveBPRStatus = (value) => {
        if (!value && value !== 0) return null;
        if (value < 0) return 'POSITIVE IMPACT';
        if (value <= 0.5) return 'NEUTRAL';
        return 'NEEDS WORK';
    };

    // Function to determine team offensive efficiency status
    const getTeamOffEfficiencyStatus = (value) => {
        if (!value) return null;
        if (value >= 110) return 'ELITE TEAM OFFENSE';
        if (value >= 100) return 'SOLID';
        return 'BELOW AVERAGE';
    };

    const themeStyles = {
        background: '#0f172a',
        cardBackground: '#1e293b',
        textPrimary: '#f1f5f9',
        textSecondary: '#94a3b8',
        border: '#334155',
        progressBackground: '#334155',
        selectBorder: '#334155',
        selectHover: '#475569'
    };

    // Title colors matching status colors
    const getValueColor = (status) => {
        if (!status) return themeStyles.textPrimary;
        return statusStyles[status]?.color || themeStyles.textPrimary;
    };

    // Removed custom tooltip show handler

    // Removed custom tooltip hide handler

    // Removed custom Tooltip component in favor of antd Tooltip

    const MetricCard = ({ title, value, subtitle, percentage, showHelp = false, isLarge = false, status }) => (
        <div
            style={{
                backgroundColor: themeStyles.cardBackground,
                borderRadius: '8px',
                padding: isLarge ? '24px' : '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                border: `1px solid ${themeStyles.border}`
            }}
        >
            <div className="flex items-center gap-2 mb-3">
                <span className='text-sm font-medium' style={{
                    color: themeStyles.textSecondary,
                    letterSpacing: '0.02em'
                }}>
                    {title}
                </span>
                {showHelp && tooltipDefinitions[title] && (
                    <SimpleTooltip title={tooltipDefinitions[title]}>
                        <HelpCircle
                            size={13}
                            style={{
                                color: themeStyles.textSecondary,
                                opacity: 0.7,
                                cursor: 'help'
                            }}
                        />
                    </SimpleTooltip>
                )}
            </div>

            <div style={{
                color: getValueColor(status),
                fontSize: '24px',
                fontWeight: '700',
                lineHeight: '1',
                marginBottom: '8px'
            }}>
                {value}
            </div>

            {subtitle && (
                <div style={{
                    color: themeStyles.textSecondary,
                    fontSize: '12px',
                    marginBottom: status ? '12px' : (percentage ? '16px' : '0')
                }}>
                    {subtitle}
                </div>
            )}

            {status && (
                <div
                    style={{
                        display: 'inline-block',
                        ...statusStyles[status],
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '0.025em',
                        marginBottom: percentage ? '12px' : '0'
                    }}
                >
                    {status}
                </div>
            )}

            {percentage ? (
                <div>
                    <div
                        style={{
                            width: '100%',
                            height: '4px',
                            backgroundColor: themeStyles.progressBackground,
                            borderRadius: '2px',
                            overflow: 'hidden'
                        }}
                    >
                        <div
                            style={{
                                width: `${percentage}%`,
                                height: '100%',
                                backgroundColor: status && statusStyles[status] ? statusStyles[status].color : '#ffd700',
                                borderRadius: '2px',
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );

    const SimpleMetricCardWithStatus = ({ title, value, subtitle, status }) => (
        <div style={{
            backgroundColor: themeStyles.cardBackground,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            border: `1px solid ${themeStyles.border}`
        }}>
            <div className="flex items-center gap-2 mb-2">
                <div className='text-sm font-medium' style={{
                    color: themeStyles.textSecondary,
                    letterSpacing: '0.02em'
                }}>
                    {title}
                </div>
                {tooltipDefinitions[title] && (
                    <SimpleTooltip title={tooltipDefinitions[title]}>
                        <HelpCircle
                            size={12}
                            style={{
                                color: themeStyles.textSecondary,
                                opacity: 0.7,
                                cursor: 'help'
                            }}
                        />
                    </SimpleTooltip>
                )}
            </div>
            <div style={{
                color: getValueColor(status),
                fontSize: title === 'Games' ? '32px' : '24px',
                fontWeight: '700',
                lineHeight: '1',
                marginBottom: '4px'
            }}>
                {value}
            </div>
            {subtitle && (
                <div style={{
                    color: themeStyles.textSecondary,
                    fontSize: '11px',
                    marginBottom: status ? '12px' : '0'
                }}>
                    {subtitle}
                </div>
            )}
            {status && (
                <div
                    style={{
                        display: 'inline-block',
                        ...statusStyles[status],
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '0.025em'
                    }}
                >
                    {status}
                </div>
            )}
        </div>
    );

    const SectionTitle = ({ children }) => (
        <h2 style={{
            color: themeStyles.textPrimary,
            fontSize: '18px',
            fontWeight: '600',
            margin: '5px 0 10px 0',
            letterSpacing: '-0.01em'
        }}>
            {children}
        </h2>
    );

    const SimpleSelect = ({ value, options, onChange, isOpen, setIsOpen }) => (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    backgroundColor: themeStyles.cardBackground,
                    border: `1px solid ${themeStyles.selectBorder}`,
                    borderRadius: '4px',
                    padding: '8px 32px 8px 12px',
                    fontSize: '14px',
                    color: themeStyles.textPrimary,
                    fontWeight: '400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minWidth: '160px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                    position: 'relative'
                }}
                onMouseEnter={(e) => {
                    if (!isOpen) e.target.style.borderColor = themeStyles.selectHover;
                }}
                onMouseLeave={(e) => {
                    if (!isOpen) e.target.style.borderColor = themeStyles.selectBorder;
                }}
            >
                <span>{value}</span>
                <ChevronDown
                    size={14}
                    style={{
                        color: themeStyles.textSecondary,
                        position: 'absolute',
                        right: '8px',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </button>

            {isOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999
                        }}
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: themeStyles.cardBackground,
                            border: `1px solid ${themeStyles.border}`,
                            borderRadius: '4px',
                            marginTop: '2px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
                            zIndex: 1000,
                            overflow: 'hidden'
                        }}
                    >
                        {options.map((option, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange(option);
                                    setIsOpen(false);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    color: themeStyles.textPrimary,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontWeight: '400',
                                    transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#404040';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const AdvancedMetricRow = ({ label, value, isHighlighted = false }) => (
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
                <span style={{
                    color: themeStyles.textSecondary,
                    fontSize: '14px',
                    fontWeight: '400'
                }}>
                    {label}
                </span>
                {tooltipDefinitions[label] && (
                    <SimpleTooltip title={tooltipDefinitions[label]}>
                        <HelpCircle
                            size={12}
                            style={{
                                color: themeStyles.textSecondary,
                                opacity: 0.7,
                                cursor: 'help'
                            }}
                        />
                    </SimpleTooltip>
                )}
            </div>
            <span style={{
                color: isHighlighted ? '#ffd700' : themeStyles.textPrimary,
                fontSize: '16px',
                fontWeight: '600'
            }}>
                {value}
            </span>
        </div>
    );

    function calculateAstToToRatio(assistRate, turnoverRate) {
        if (turnoverRate === 0) {
            return 'N/A'; // or Infinity, depending on how you want to handle divide by zero
        }
        return parseFloat(assistRate / turnoverRate);
    }

    return (
        <div style={{
            backgroundColor: themeStyles.background,
        }}>
            {/* Removed custom tooltip */}
            <div style={{ margin: '0 auto' }}>
                {/* Basic Metrics */}
                <SectionTitle>Overall Metrics</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-6 mb-4">
                    <SimpleMetricCardWithStatus
                        title="Games"
                        value={stats?.games ? stats.games.toString() : 'N/A'}
                        subtitle="Games Played"
                    />
                    <SimpleMetricCardWithStatus
                        title="MPG"
                        value={stats?.minutes_per_game ? stats.minutes_per_game.toString() : 'N/A'}
                        subtitle="Minutes Per Game"
                        status={getMPGStatus(stats?.minutes_per_game)}
                    />
                    <SimpleMetricCardWithStatus
                        title="PPG"
                        value={stats?.points_per_game ? stats.points_per_game.toString() : 'N/A'}
                        subtitle="Points Per Game"
                        status={getPPGStatusOverall(stats?.points_per_game)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Efficiency Rating"
                        value={stats?.Eff ? stats.Eff.toString() : 'N/A'}
                        subtitle="Player Efficiency Rating"
                        status={getEfficiencyRatingStatus(stats?.Eff)}
                    />
                    <SimpleMetricCardWithStatus
                        title="TOPG"
                        value={stats?.turnovers_per_game ? stats.turnovers_per_game.toString() : 'N/A'}
                        subtitle="Turnovers Per Game"
                        status={getTOPGStatus(stats?.turnovers_per_game)}
                    />
                    <SimpleMetricCardWithStatus
                        title="FPG"
                        value={stats.fouls_per_game.toString() || 'N/A'}
                        subtitle="Fouls Per Game"
                        status={getFPGStatus(stats?.fouls_per_game)}
                    />
                </div>
                {/* Shooting Performance */}
                <SectionTitle>Shooting Performance</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
                    <MetricCard
                        title="3-Point Percentage"
                        value={stats?.threePointPct ? `${stats.threePointPct}%` : 'N/A'}
                        subtitle={`League Average: 35.0%`}
                        percentage={stats?.threePointPct || 0}
                        showHelp={true}
                        isLarge={false}
                        status={getThreePointStatus(stats?.threePointPct)}
                    />
                    <MetricCard
                        title="2-Point Percentage"
                        value={stats?.twoPointPct ? `${stats.twoPointPct}%` : 'N/A'}
                        subtitle="League Average: 51.0%"
                        percentage={stats?.twoPointPct || 0}
                        showHelp={true}
                        isLarge={false}
                        status={getTwoPointStatus(stats?.twoPointPct)}
                    />
                    <MetricCard
                        title="Free Throw Percentage"
                        value={stats?.ftPct ? `${stats.ftPct}%` : 'N/A'}
                        subtitle="League Average: 71.0%"
                        percentage={stats?.ftPct || 0}
                        showHelp={true}
                        isLarge={false}
                        status={getFreeThrowStatus(stats?.ftPct)}
                    />
                    <MetricCard
                        title="Effective Field Goal %"
                        value={stats?.effective_field_goal_percentage ? `${stats.effective_field_goal_percentage}%` : "N/A"}
                        subtitle="Efficiency metric"
                        percentage={stats?.effective_field_goal_percentage || 0}
                        showHelp={true}
                        isLarge={false}
                        status={getEffectiveFieldGoalStatus(stats?.effective_field_goal_percentage)}
                    />
                </div>

                {/* Scoring Breakdown - Updated to individual cards */}
                <SectionTitle>Scoring Breakdown</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                    <SimpleMetricCardWithStatus
                        title="Points Per Game"
                        value={stats?.scoring ? stats.scoring.toString() : 'N/A'}
                        subtitle="Average points scored per game"
                        status={getPointsPerGameStatus(stats?.scoring, stats?.fga)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Field Goal Percentage"
                        value={stats?.field_goal_percentage || 'N/A'}
                        subtitle="Overall shooting accuracy"
                        status={getFieldGoalStatus(stats?.field_goal_percentage)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Scoring Efficiency"
                        value={stats?.Eff || 'N/A'}
                        subtitle="Composite scoring efficiency"
                        status={getScoringEfficiencyStatus(stats?.field_goal_percentage, stats?.trueShooting, stats?.fga)}
                    />
                </div>

                {/* Ball Handling & Distribution */}
                <SectionTitle>Ball Handling & Distribution</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
                    <SimpleMetricCardWithStatus
                        title="Assist Rate"
                        value={stats?.assistRate ? `${stats.assistRate}%` : 'N/A'}
                        subtitle="Percentage of teammate FGs assisted"
                        status={getAssistRateStatus(stats?.assistRate)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Turnover Rate"
                        value={stats?.turnoverRate ? `${stats.turnoverRate}%` : 'N/A'}
                        subtitle="Ball security metric"
                        status={getTurnoverRateStatus(stats?.turnoverRate)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Playmaking Score"
                        value={stats?.playmaking ? stats.playmaking.toString() : 'N/A'}
                        subtitle="Composite playmaking metric"
                        status={getPlaymakingStatus(stats?.playmaking)}
                    />
                    <SimpleMetricCardWithStatus
                        title="Assist to TO Ratio"
                        value={`${calculateAstToToRatio(stats?.assistRate, stats?.turnoverRate)}%` || 'N/A'}
                        subtitle="Decision making metric"
                        status={getAssistToTurnoverStatus(calculateAstToToRatio(stats?.assistRate, stats?.turnoverRate))}
                    />
                </div>

                {/* Offensive Efficiency Metrics */}
                <SectionTitle>Offensive Efficiency Metrics</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                    <MetricCard
                        title="Offensive BPR"
                        value={stats?.obprPredicted ? stats.obprPredicted.toString() : 'N/A'}
                        subtitle="Points above average per 100"
                        showHelp={true}
                        status={getOffensiveBPRStatus(stats?.obprPredicted)}
                    />
                </div>
            </div>
        </div>
    );
};

export default OffensiveTab;